import { useState } from 'react';
import { Button, ConfirmDialog, Input } from '@/shared/ui';
import { useToast } from '@/shared/ui/Toast';
import { useResetAgent, useResetLocationAgents } from '../../hooks/useCommandCenter';
import {
  resetConfirmMatches,
  resetErrorMessage,
  type ResetLocation,
} from '../../../agents/utils/resetConfirm';
import styles from './ResetRehireButton.module.css';

/**
 * "Reset & rehire" with its confirm-by-typing dialog, in two scopes:
 *
 * - `agent` given: wipes only that agent's work at the location (its tasks, drafts, messages and
 *   run history) and hires it again. The other agents, client facts, work schedule settings and
 *   connections are kept. The operator types the agent's name.
 * - no `agent`: wipes everything every agent knows about the location and hires every active
 *   agent there again, so the first audits, drafts and the day-one report run as they do for a new
 *   practice. The operator types the location's name.
 */
export function ResetRehireButton({
  practiceId,
  location,
  agent,
  label = 'Reset & rehire',
  disabled = false,
  variant = 'destructive',
}: {
  practiceId: string;
  location: ResetLocation;
  agent?: { roleKey: string; agentName: string };
  label?: string;
  disabled?: boolean;
  variant?: 'destructive' | 'ghost';
}) {
  const toast = useToast();
  const resetLocation = useResetLocationAgents(practiceId);
  const resetAgent = useResetAgent(practiceId);
  const pending = resetLocation.isPending || resetAgent.isPending;
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const confirmText = agent ? agent.agentName : location.locationName;
  const matches = resetConfirmMatches(typed, confirmText);

  const close = () => {
    if (pending) return;
    setOpen(false);
    setTyped('');
    setError(null);
  };

  const confirm = async () => {
    if (!matches) return;
    setError(null);
    try {
      if (agent) {
        await resetAgent.mutateAsync({
          locationId: location.locationId,
          roleKey: agent.roleKey,
          confirmName: typed,
        });
        toast.success(
          `${agent.agentName} at ${location.locationName}: wiped and re-hired. The other agents were not touched.`,
        );
      } else {
        const result = await resetLocation.mutateAsync({
          locationId: location.locationId,
          confirmName: typed,
        });
        const left = result.skipped?.map((s) => s.reason).join(' ');
        toast.success(
          `${location.locationName}: agent data wiped and ${result.rehiredRoleKeys.length} agent(s) re-hired. First audits start shortly.${left ? ` Not reset: ${left}` : ''}`,
        );
      }
      setOpen(false);
      setTyped('');
    } catch (err) {
      // Keep the dialog open so the operator sees why it failed and can retry without retyping.
      setError(resetErrorMessage(err));
    }
  };

  return (
    <>
      <Button
        variant={variant}
        size="sm"
        disabled={disabled || pending}
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      >
        {pending ? 'Resetting…' : label}
      </Button>
      <ConfirmDialog
        isOpen={open}
        onClose={close}
        onConfirm={confirm}
        title={
          agent
            ? `Reset & rehire ${agent.agentName} at ${location.locationName}?`
            : `Reset & rehire every agent at ${location.locationName}?`
        }
        variant="destructive"
        confirmLabel={agent ? `Wipe and re-hire ${agent.agentName}` : 'Wipe and re-hire all'}
        isLoading={pending}
        confirmDisabled={!matches || pending}
        message={
          <div className={styles.confirmBody}>
            <span>
              {agent
                ? `This deletes ${agent.agentName}'s tasks, drafts, messages and run history for this location, then hires ${agent.agentName} again. The other agents, client facts, work schedule settings and connections are kept. It cannot be undone.`
                : `This deletes every task, draft, message, audit, client fact and report the agents hold for this location, then re-hires the agents that can start again (${location.agentNames.join(' and ')}). An agent that cannot start yet, for example because its ad connection is broken, is skipped and listed afterwards. Work schedule settings (frequency and paused work) and connections are kept. It cannot be undone.`}
            </span>
            <Input
              label={`Type "${confirmText}" to confirm`}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoFocus
              fullWidth
            />
            {error && <div className={styles.errorBanner}>{error}</div>}
          </div>
        }
      />
    </>
  );
}
